import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { ReactComponent as Logo } from './assets/OncampusLogo.svg';
import { Button } from '../Button/Button';
import { RefObject, KeyboardEvent as ReactKeyboardEvent } from 'react';

type SectionId = 'WHYOCA' | 'FAQS' | 'BLOG' | 'CONTACT';
type SectionRefs = Record<SectionId, RefObject<HTMLElement>>;

interface TopBarProps {
  sectionRefs: SectionRefs;
  isLanding?: boolean;
}

const TopBar: React.FC<TopBarProps> = ({ sectionRefs, isLanding }) => {
  const topBarItems: { name: string; id: SectionId; controlID: string }[] = [
    { name: 'Why OnCampus', id: 'WHYOCA', controlID: 'nav-menu-/why-oncampus' },
    { name: 'FAQS', id: 'FAQS', controlID: 'nav-menu-/faq' },
    {
      name: 'Blog',
      id: 'BLOG',
      controlID: 'nav-menu-https://blog.oncampusabroad.com/',
    },
    { name: 'Contact', id: 'CONTACT', controlID: 'nav-menu-/contact' },
  ];

  const BLOG_URL = 'https://blog.oncampusabroad.com/';
  const textButton = 'Buy Insurance';
  const hrefButton = '/onboarding';
  const idButton = 'NavigationMenu_BUY-INSURANCE_Button';

  const handleScroll = (id: SectionId) => {
    if (id === 'BLOG') return;

    if (window.location.pathname === '/') {
      sectionRefs[id]?.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = `/?id=${id}`;
    }
  };

  const handleKeyDownMenuItem = (e: ReactKeyboardEvent, id: SectionId) => {
    if (e.key !== 'Enter') return;
    handleScroll(id);
  };

  return createPortal(
    <TopBarContainer>
      <Wrapper>
        <a href='/' id='nav-menu-/logo'>
          <LogoOca />
        </a>
        {isLanding === true && (
          <Menu>
            {topBarItems.map((item) =>
              item.id === 'BLOG' ? (
                <MenuItem
                  as='a'
                  key={item.id}
                  href={BLOG_URL}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label='Abrir Blog en nueva pestaña'
                  id={item.controlID}
                >
                  {item.name}
                </MenuItem>
              ) : (
                <MenuItem
                  key={item.id}
                  onClick={() => handleScroll(item.id)}
                  onKeyDown={(e) => handleKeyDownMenuItem(e, item.id)}
                  aria-label={`Ir a ${item.name}`}
                  id={item.controlID}
                >
                  {item.name}
                </MenuItem>
              )
            )}
          </Menu>
        )}
        {isLanding === true && (
          <CustomButton
            href={hrefButton}
            backgroundColor='#FFF6EF'
            textColor='#283288'
            id={idButton}
          >
            {textButton}
          </CustomButton>
        )}
      </Wrapper>
    </TopBarContainer>,
    document.body
  );
};

export default TopBar;

const TopBarContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding-inline: 32px;
  position: fixed;
  top: 55px;
  z-index: 99999;
`;

const Wrapper = styled.div`
  max-width: 1248px;
  width: 100%;
  height: 95px;
  background: #283288;
  border-radius: 47px;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 28px 40px;
  gap: 30px;
  margin: 0 auto;
  border: 1px solid rgba(255, 255, 255, 0.3);

  @media (max-width: 1024px) {
    gap: 16px;
    padding: 20px 16px;
    height: auto;
    max-width: 774px;
  }
`;

const CustomButton = styled(Button)`
  color: #283288 !important;
  max-width: 175px;
  font-size: 20px;
  white-space: nowrap;
  height: auto;
  padding: 10px 10px;

  @media (max-width: 1024px) {
    width: 115px;
    font-size: 14px;
    padding: 10px 14px;
    height: auto;
  }
`;

const LogoOca = styled(Logo)`
  min-width: 200px;

  @media (max-width: 1024px) {
    max-width: 160px;
  }
`;

const Menu = styled.nav`
  display: flex;
  gap: 46px;

  @media (max-width: 1024px) {
    gap: 24px;
  }
`;

const MenuItem = styled.button`
  all: unset;
  color: #fff6ef;
  text-align: center;
  font-family: 'League Spartan';
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  transition: opacity 0.2s ease;
  white-space: nowrap;
  text-decoration: none;

  &:hover {
    opacity: 0.7;
  }

  @media (max-width: 1024px) {
    font-size: 14px;
  }
`;
